/**
 * `getLocalFromUtc` receives a utc date string and return a local date string.
 * @param {string} utcDate - date to be procesed.
 * @returns {string} - date on local zone.
 */

import { format, parseISO } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { es } from "date-fns/locale";

export const getLocalFromUtc = (utcDate: string): string => {
  const parsedTime = parseISO(utcDate);
  const date = utcToZonedTime(
    parsedTime,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );

  return format(date, "yyyy-MM-dd'T'HH:mm");
};

/**
 * `getUtcFromLocal` receives a loca date string and return a utc date string.
 * @param {string} localDate - date to be procesed.
 * @returns {string} - date on utc.
 */

export const getUtcFromLocal = (localDate: string) => {
  const parsedTime = parseISO(localDate);

  const date = zonedTimeToUtc(
    parsedTime,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const utc = new Date(date).toISOString();

  return isoStringToFormat(utc);
};

//el 25 de 3 a 10

export const formatDate = (
  date: string,
  dateFormat = "dd'/'MM'/'yy' 'HH:mm ",
): string => {
  try {
    const formatedDate = format(new Date(date), dateFormat, {
      locale: es,
    });
    return formatedDate;
  } catch (error) {
    return "sin fecha";
  }
};

export const formatShortDate = (date: string): string => {
  try {
    const formatedDate = format(new Date(date), "dd'/'MM'/'yyyy", {
      locale: es,
    });
    return formatedDate;
  } catch (error) {
    return "sin fecha";
  }
};
const isoStringToFormat = (string: string) => {
  //deleting Z and .000
  const stringWithoutZ = string.slice(0, -5);
  //deleting T
  const dateAndTime = stringWithoutZ.split("T");

  return `${dateAndTime[0]} ${dateAndTime[1]}`;
};

export const getSinceDate = (date: string): string => {
  const localDate = getLocalFromUtc(date);
  // Fecha de referencia
  const fechaReferencia = new Date(localDate);

  // Fecha actual
  const fechaActual = new Date();

  // Diferencia en milisegundos
  const diferenciaMilisegundos =
    fechaActual.getTime() - fechaReferencia.getTime();

  // Convertir la diferencia a días, horas, minutos y segundos
  const segundos = Math.floor(diferenciaMilisegundos / 1000);
  const minutos = Math.floor(segundos / 60);
  const horas = Math.floor(minutos / 60);
  const dias = Math.floor(horas / 24);

  if (dias > 0) {
    return `Hace ${dias} ${dias === 1 ? "Día" : "Días"}`;
  }
  if (horas > 0) {
    return `Hace ${horas} ${horas === 1 ? "Hora" : "Horas"}`;
  }
  if (minutos > 0) {
    return `Hace ${minutos} ${minutos === 1 ? "Minuto" : "Minutos"}`;
  }

  if (segundos < 0) {
    return `Fecha a futuro`;
  }

  return `Hace ${segundos} ${segundos === 1 ? "Segundo" : "Segundos"}`;
};

export const getUrlFormatDate = (date: string): string => {
  const newString = date.replaceAll(":", "%3A");
  return newString;
};

export const formatCardDate = (date: string): string => {
  try {
    const centerHour = date.split(".")[0];
    const fechaEnCDMX = new Date(centerHour + "-06:00");
    return format(fechaEnCDMX, "dd'/'MM'/'yyyy ' ' HH:mm 'hrs.'");
  } catch (error) {
    return "Sin fecha";
  }
};

export function calculateDays(start: string, end: string): number {
  // Convertir la fecha inicial a un objeto Date
  const startDate = new Date(start);

  // Obtener la fecha actual
  const endDate = new Date(end);

  // Calcular la diferencia en milisegundos
  const diferenciaTiempo = endDate.getTime() - startDate.getTime();

  // Convertir la diferencia de milisegundos a días
  const diasPasados = Math.floor(diferenciaTiempo / (1000 * 60 * 60 * 24));

  return diasPasados;
}

export function calculateMinutes(start: string, end: string): number {
  // Convertir la fecha inicial a un objeto Date
  const startDate = new Date(start);

  // Obtener la fecha actual
  const endDate = new Date(end);

  // Calcular la diferencia en milisegundos
  const diferenciaTiempo = endDate.getTime() - startDate.getTime();

  // Convertir la diferencia de milisegundos a días
  const diasPasados = Math.floor(diferenciaTiempo / (1000 * 60));

  return diasPasados;
}

export const formatTime = (date: Date): string => {
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

// Returns the local HH:mm of a datetime string for a time input; leaves an
// already-HH:mm (or invalid) value untouched so it is idempotent.
export const extractTimeFromDate = (datetime: string): string => {
  const date = new Date(datetime);
  if (isNaN(date.getTime())) {
    return datetime;
  }
  return formatTime(date);
};

export const formatDateForInput = (date: string): string => {
  const dateObject = new Date(date);
  const year = dateObject.getFullYear();
  const month = String(dateObject.getMonth() + 1).padStart(2, "0");
  const day = String(dateObject.getDate()).padStart(2, "0");
  const hour = String(dateObject.getHours()).padStart(2, "0");
  const minute = String(dateObject.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hour}:${minute}`;
};

export const formatTimeWithSeconds = (dateString: string): string => {
  try {
    const parsedTime = parseISO(dateString);
    const date = utcToZonedTime(
      parsedTime,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    );
    const localDate = format(date, "yyyy-MM-dd'T'HH:mm:ss");
    const parsedDate = new Date(localDate);

    const hours = String(parsedDate.getHours()).padStart(2, "0");
    const minutes = String(parsedDate.getMinutes()).padStart(2, "0");
    const seconds = String(parsedDate.getSeconds()).padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  } catch (error) {
    return "00:00:00";
  }
};

export const formatDateWithMonthName = (date: string): string => {
  try {
    const parsedTime = parseISO(date);
    const localDate = utcToZonedTime(
      parsedTime,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    );
    const formatedDate = format(localDate, "dd MMM yyyy, HH:mm 'hrs'", {
      locale: es,
    });
    return formatedDate;
  } catch (error) {
    return "sin fecha";
  }
};

export const formatLastUpdateDate = (date: string): string => {
  try {
    const parsedTime = parseISO(date);
    const localDate = utcToZonedTime(
      parsedTime,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    );

    const formatedDate = format(localDate, "HH:mm 'hrs del' dd MMM yyyy", {
      locale: es,
    });

    return `Últ. ${formatedDate}`;
  } catch (error) {
    return "Últ. sin fecha";
  }
};

/**
 * `getDateRange` recibe un string de duración y devuelve un objeto con start_date y end_date
 * @param {string} duration - duración en formato "1h", "2h", "3h", "4h", "1d", "1s", "2s", "1m", "2m"
 * @returns {object} - objeto con start_date y end_date en formato UTC
 */
export const getDateRange = (
  duration: string,
): { start_date: string; end_date: string } => {
  const now = new Date();
  const endDate = now.toISOString();

  let startDate: Date;

  const match = duration.match(/^(\d+)([hdms])$/);
  if (!match) {
    throw new Error(
      'Formato de duración inválido. Use formatos como "1h", "2h", "3h", "4h", "1d", "1s", "2s", "1m", "2m"',
    );
  }

  const [, amount, unit] = match;
  const amountNum = parseInt(amount, 10);

  switch (unit) {
    case "h":
      startDate = new Date(now.getTime() - amountNum * 60 * 60 * 1000);
      break;
    case "d":
      startDate = new Date(now.getTime() - amountNum * 24 * 60 * 60 * 1000);
      break;
    case "s":
      startDate = new Date(now.getTime() - amountNum * 7 * 24 * 60 * 60 * 1000);
      break;
    case "m":
      startDate = new Date(now);
      startDate.setMonth(now.getMonth() - amountNum);
      break;
    default:
      throw new Error(
        'Unidad de tiempo no soportada. Use "h" para horas, "d" para días, "s" para semanas o "m" para meses',
      );
  }

  return {
    start_date: startDate.toISOString(),
    end_date: endDate,
  };
};
